import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { ChatThreadOmni } from '@airia/ui/chat';
import { ChatBarV2, useChatBarV2Context, useChatMessages, useChatMessageRenderer } from '@airia/ui';
import { Plus } from 'lucide-react';
import { useAddinStream } from '../../hooks/useAddinStream';
import { useFileUpload } from '../../hooks/useFileUpload';
import { useMcpSessionId, useBrowserAgentVariables } from '../McpSessionProvider';
import { ProfileDropdown } from '../playbook/ProfileDropdown';
import { SignalRStatusDot } from '../SignalRStatusDot';
import { AddinAssistantMessage } from './AddinAssistantMessage';
import { AddinChatActionsProvider } from './AddinChatActionsContext';
import { CompactingCard } from './CompactingCard';
import { customMarkdownComponents } from './custom-markdown';
// Padding offsets
const PADDING_OFFSET = 20;
const STREAMING_PADDING_OFFSET = 60;
// Default gradient background from embed-new
const DEFAULT_BACKGROUND = 'radial-gradient(ellipse at top left, rgba(129, 140, 248, 0.08) 0%, transparent 50%), radial-gradient(ellipse at bottom right, rgba(129, 140, 248, 0.08) 0%, transparent 50%), white';
// File upload trigger component
function FileUploadTrigger({ disabled }) {
    const { addFiles } = useChatBarV2Context();
    const handleFileClick = useCallback(() => {
        const input = document.createElement('input');
        input.type = 'file';
        input.multiple = true;
        input.accept = 'image/*,.pdf,.doc,.docx,.txt,.csv,.json,.xml';
        input.style.display = 'none';
        input.onchange = (e) => {
            const files = e.target.files;
            if (files && files.length > 0) {
                addFiles(Array.from(files));
            }
            input.remove();
        };
        document.body.appendChild(input);
        input.click();
    }, [addFiles]);
    return (_jsx(ChatBarV2.ActionButton, { disabled: disabled, onClick: handleFileClick, children: _jsx(Plus, { className: "size-4" }) }));
}
// Layout component using Input (textarea)
function ChatBarLayout({ isStreaming, isCompact }) {
    const { inputRef, onExpand, value, compact, isExpanded } = useChatBarV2Context();
    const wasStreamingRef = useRef(isStreaming);
    const wasCompactRef = useRef(isCompact);
    const hasExpandedRef = useRef(false);
    // Focus input when streaming ends (transitions from true to false)
    useEffect(() => {
        if (wasStreamingRef.current && !isStreaming) {
            // Small delay to ensure the input is re-enabled before focusing
            setTimeout(() => {
                inputRef?.current?.focus();
            }, 50);
        }
        wasStreamingRef.current = isStreaming;
    }, [isStreaming, inputRef]);
    // Auto-expand when content overflows or contains newlines (like LexicalInput's OverflowDetectionPlugin)
    useEffect(() => {
        // Only check in compact mode and if not already expanded
        if (!compact || isExpanded || hasExpandedRef.current)
            return;
        const textarea = inputRef?.current;
        if (!textarea)
            return;
        // Check for explicit newlines
        if (value.includes('\n')) {
            hasExpandedRef.current = true;
            onExpand?.();
            return;
        }
        // Check for overflow (content wider than container)
        if (textarea.scrollHeight > textarea.clientHeight) {
            hasExpandedRef.current = true;
            onExpand?.();
        }
    }, [value, compact, isExpanded, onExpand, inputRef]);
    // Reset expansion tracking when compact mode changes
    useEffect(() => {
        if (!compact) {
            hasExpandedRef.current = false;
        }
    }, [compact]);
    // Force repaint when transitioning from compact to expanded
    useEffect(() => {
        if (wasCompactRef.current && !isCompact && inputRef?.current) {
            // Simulate typing and deleting to fix placeholder rendering
            const textarea = inputRef.current;
            setTimeout(() => {
                const nativeInputValueSetter = Object.getOwnPropertyDescriptor(window.HTMLTextAreaElement.prototype, 'value')?.set;
                if (nativeInputValueSetter) {
                    nativeInputValueSetter.call(textarea, ' ');
                    textarea.dispatchEvent(new Event('input', { bubbles: true }));
                    nativeInputValueSetter.call(textarea, '');
                    textarea.dispatchEvent(new Event('input', { bubbles: true }));
                }
            }, 50);
        }
        wasCompactRef.current = isCompact;
    }, [isCompact, inputRef]);
    const getSendVariant = () => {
        if (isStreaming)
            return 'stop';
        return undefined;
    };
    // Only autoFocus in compact mode to avoid placeholder rendering issues
    return (_jsxs(ChatBarV2.Layout, { children: [_jsx(ChatBarV2.Input, { autoFocus: isCompact, placeholder: "Ask anything..." }), _jsx(ChatBarV2.ExtraContent, { children: _jsx(ChatBarV2.FileAttachmentView, {}) }), _jsx(ChatBarV2.Actions, { children: _jsx(FileUploadTrigger, { disabled: isStreaming }) }), _jsx(ChatBarV2.SendButton, { variant: getSendVariant() })] }));
}
export function AddinChat({ agentId, bearerToken, apiUrl, userId, hostAdapter, greeting = "Hello! I'm here to help you with your document. What would you like to know?", }) {
    const mcpSessionId = useMcpSessionId();
    const browserAgentVariables = useBrowserAgentVariables();
    const [conversationId] = useState(() => uuidv4());
    const [value, setValue] = useState('');
    const scrollViewportRef = useRef(null);
    const [scrollViewportHeight, setScrollViewportHeight] = useState(0);
    // Measure scroll viewport height for lastMessagesMinHeight calculation
    useEffect(() => {
        const el = scrollViewportRef.current;
        if (!el)
            return;
        const updateHeight = () => setScrollViewportHeight(el.clientHeight);
        updateHeight();
        const observer = new ResizeObserver(updateHeight);
        observer.observe(el);
        return () => observer.disconnect();
    }, []);
    // File upload handling
    const { uploadFiles, getFileUrls, clearFiles, isUploading } = useFileUpload({
        apiUrl,
        bearerToken,
        onError: (err) => {
            console.error('[AddinChat] File upload error:', err);
        },
    });
    const { messages, events, submit, isStreaming, isCompacting, error, typewriterState, streamingMessageId, } = useAddinStream({
        agentId,
        bearerToken,
        apiUrl,
        conversationId,
        variables: browserAgentVariables,
        onError: (err) => {
            console.error('[AddinChat] Stream error:', err);
        },
    });
    // Upload files when added to ChatBarV2
    const handleFilesAdd = useCallback(async (newFiles) => {
        await uploadFiles(newFiles);
    }, [uploadFiles]);
    const { displayMessages, hasMessages } = useChatMessages({
        messages,
        greeting,
        isLoading: isStreaming,
        error,
        events: events,
    });
    // Exclude the streaming message from stable rendering — it's handled by AddinAssistantMessage
    const stableMessages = useMemo(() => {
        if (!streamingMessageId)
            return displayMessages;
        return displayMessages.filter((m) => m.id !== streamingMessageId);
    }, [displayMessages, streamingMessageId]);
    const renderedMessages = useChatMessageRenderer({
        messages: stableMessages,
        isStreaming: false,
        // Picker tags emitted in previous turns keep rendering as React
        // components after the stream ends. @airia/ui's customTagsPlugin
        // extracts them and routes through the components map.
        markdownProps: {
            enableCustomTags: true,
            components: customMarkdownComponents,
        },
    });
    // Show the custom assistant message component during streaming and while awaiting response
    const showAssistantMessage = isStreaming;
    // Check if there's an active conversation (user has sent at least one message)
    const hasUserMessages = useMemo(() => {
        return displayMessages.some((m) => m.role === 'user');
    }, [displayMessages]);
    // Min-height for the last 2 messages so auto-scroll-to-bottom
    // positions the user's message at the top of the viewport
    const lastMessagesMinHeight = useMemo(() => {
        if (!hasUserMessages || !scrollViewportHeight)
            return undefined;
        const padding = isStreaming ? STREAMING_PADDING_OFFSET : PADDING_OFFSET;
        return `${scrollViewportHeight - padding}px`;
    }, [hasUserMessages, scrollViewportHeight, isStreaming]);
    // Dynamic bottom padding that increases during streaming
    const bottomPadding = useMemo(() => {
        return isStreaming ? STREAMING_PADDING_OFFSET : PADDING_OFFSET;
    }, [isStreaming]);
    // Core submission path shared by the user-typed flow (handleSubmit) and
    // programmatic submissions triggered by interactive custom markdown
    // components (MessageRadios/Dropdown/Checkboxes/Button via AddinChatActions).
    const submitMessageWithText = useCallback(async (rawText) => {
        const trimmedValue = rawText.trim();
        if (!trimmedValue)
            return;
        // Clear the input regardless of which path called us so a user
        // clicking a radio option doesn't see stale draft text linger.
        setValue('');
        const fileUrls = getFileUrls();
        const hasFiles = fileUrls.images.length > 0 || fileUrls.files.length > 0;
        clearFiles();
        // Skip sending document context when ACC has an active SignalR
        // connection (it can fetch document content directly via MCP tools).
        if (mcpSessionId) {
            await submit(trimmedValue, undefined, userId, hasFiles ? fileUrls : undefined);
            return;
        }
        try {
            const documentContext = await hostAdapter.getDocumentContext();
            await submit(trimmedValue, documentContext.content, userId, hasFiles ? fileUrls : undefined);
        }
        catch (err) {
            console.error('[AddinChat] Error getting document context:', err);
            // Still submit the message even if document context fails
            await submit(trimmedValue, undefined, userId, hasFiles ? fileUrls : undefined);
        }
    }, [submit, hostAdapter, userId, getFileUrls, clearFiles, mcpSessionId]);
    const handleSubmit = useCallback(() => submitMessageWithText(value), [submitMessageWithText, value]);
    const handleChange = (newValue) => {
        setValue(newValue);
    };
    return (_jsx(AddinChatActionsProvider, { submitMessage: submitMessageWithText, isExecuting: isStreaming, children: _jsxs("div", { "data-testid": "addin-chat", className: "h-full flex flex-col", style: { background: DEFAULT_BACKGROUND }, children: [_jsx("div", { "data-testid": "chat-messages", ref: scrollViewportRef, className: "flex-1 min-h-0 overflow-hidden", children: _jsxs(ChatThreadOmni, { className: "h-full", autoScroll: true, showScrollButton: true, isStreaming: isStreaming, loading: isStreaming, lastMessagesMinHeight: lastMessagesMinHeight, additionalBottomPadding: bottomPadding, emptyState: !hasMessages ? (_jsx("div", { className: "text-muted-foreground text-sm", children: "Start a conversation by typing a message below." })) : undefined, children: [renderedMessages, showAssistantMessage && (_jsx("div", { "data-testid": "chat-assistant-streaming", children: _jsx(AddinAssistantMessage, { events: events, pipelineId: agentId, isStreaming: isStreaming, typewriterText: typewriterState.displayedText }) })), isCompacting && _jsx(CompactingCard, { isCompacting: isCompacting })] }) }), _jsxs("div", { "data-testid": "chat-input-area", className: "p-2 shrink-0 flex flex-col items-center border-t", children: [_jsx(ChatBarV2, { onInputChange: handleChange, onSend: handleSubmit, onFilesAdd: handleFilesAdd, disabled: isStreaming || isUploading, sendDisabled: isStreaming || isUploading || !value.trim(), compact: !hasUserMessages, children: _jsx(ChatBarLayout, { isStreaming: isStreaming || isUploading, isCompact: !hasUserMessages }) }), _jsxs("div", { className: "relative flex items-center w-full pt-3", children: [_jsx("div", { className: "absolute left-0", children: _jsx(ProfileDropdown, {}) }), _jsx("p", { className: "text-xs text-muted-foreground w-full text-center", children: "AI can make mistakes. Check important info." }), _jsx("div", { className: "absolute right-0", children: _jsx(SignalRStatusDot, {}) })] })] })] }) }));
}
